import { gql } from 'graphql-tag';

// ─── Base Fragments ─────────────────────────

export const SeasonsFields = gql`
  fragment SeasonsFields on Season {
    id
    year
  }
`;

export const SeasonsWithMetaFields = gql`
  fragment SeasonsWithMetaFields on Season {
    id
    year
    current
    hasDraft
    hasNegotiations
  }
`;

export const AdjustmentFields = gql`
  fragment AdjustmentFields on Adjustment {
    __typename
    id
    amount
    note
    season {
      ...SeasonsFields
    }
    player {
      id
      name
    }
  }
  ${SeasonsFields}
`;

export const DesignationFields = gql`
  fragment DesignationFields on Designation {
    id
    name
    order
  }
`;

export const FranchiseFields = gql`
  fragment FranchiseFields on Franchise {
    id
    name
    logo
    abbr
  }
`;

export const CapSpaceFields = gql`
  fragment CapSpaceFields on CapSpace {
    __typename
    id
    amount
    note
    recipient {
      ...FranchiseFields
    }
    owner {
      ...FranchiseFields
    }
    season {
      ...SeasonsFields
    }
  }
  ${SeasonsFields}
  ${FranchiseFields}
`;

export const ContractPlayerFields = gql`
  fragment ContractPlayerFields on Player {
    id
    name
    position
    team
    adp
  }
`;

export const ContractFields = gql`
  fragment ContractFields on Contract {
    id
    salary
    designation {
      ...DesignationFields
    }
    franchise {
      ...FranchiseFields
    }
    player {
      ...ContractPlayerFields
    }
  }
  ${FranchiseFields}
  ${DesignationFields}
  ${ContractPlayerFields}
`;

export const PlayerFields = gql`
  fragment PlayerFields on Player {
    id
    name
    position
    team
    years
    contract {
      ...ContractFields
    }
  }
  ${ContractFields}
`;

export const DraftPickFields = gql`
  fragment DraftPickFields on DraftPick {
    id
    pick
    round
    salary
    skipped
    season {
      ...SeasonsFields
    }
    designation {
      ...DesignationFields
    }
    franchise {
      ...FranchiseFields
    }
    originalFranchise {
      ...FranchiseFields
    }
    player {
      ...PlayerFields
    }
  }
  ${FranchiseFields}
  ${PlayerFields}
  ${SeasonsFields}
  ${DesignationFields}
`;

// ─── Asset Fragments ─────────────────────────

export const PlayerAssetFields = gql`
  fragment PlayerAssetFields on PlayerAsset {
    __typename
    id
    previousDesignation
    previousSalary
    previousFranchise
    salary
    designation {
      ...DesignationFields
    }
    to {
      ...FranchiseFields
    }
    from {
      ...FranchiseFields
    }
    contract {
      id
      salary
    }
    player {
      ...PlayerFields
    }
  }
  ${FranchiseFields}
  ${PlayerFields}
  ${DesignationFields}
`;

export const CapSpaceAssetFields = gql`
  fragment CapSpaceAssetFields on CapSpaceAsset {
    __typename
    id
    salary
    note
    to {
      ...FranchiseFields
    }
    from {
      ...FranchiseFields
    }
    season {
      ...SeasonsFields
    }
  }
  ${FranchiseFields}
  ${SeasonsFields}
`;

export const AdjustmentAssetFields = gql`
  fragment AdjustmentAssetFields on AdjustmentAsset {
    __typename
    id
    salary
    note
    previousDesignation
    previousSalary
    previousFranchise
    adjustmenPlayer: player {
      id
      name
    }
    adjustment {
      id
    }
    to {
      ...FranchiseFields
    }
    season {
      ...SeasonsFields
    }
  }
  ${FranchiseFields}
  ${SeasonsFields}
`;

export const DraftPickAssetFields = gql`
  fragment DraftPickAssetFields on DraftPickAsset {
    __typename
    id
    to {
      ...FranchiseFields
    }
    from {
      ...FranchiseFields
    }
    draftPick {
      ...DraftPickFields
    }
    draftedPlayer {
      ...PlayerFields
    }
  }
  ${FranchiseFields}
  ${DraftPickFields}
  ${PlayerFields}
`;

// ─── Transaction Fragment ───────────────────

export const FullTransactionFields = gql`
  fragment FullTransactionFields on Transaction {
    id
    type
    date
    status
    rejectionMessages
    note
    assets {
      __typename
      ...PlayerAssetFields
      ...AdjustmentAssetFields
      ...CapSpaceAssetFields
      ...DraftPickAssetFields
    }
  }
  ${PlayerAssetFields}
  ${AdjustmentAssetFields}
  ${CapSpaceAssetFields}
  ${DraftPickAssetFields}
`;

export const FranchiseExpandedFields = gql`
  fragment FranchiseExpandedFields on Franchise {
    ...FranchiseFields
    phone
    order
    motto
    color
    availableCapSpace
    availableRosterSpots
    owner {
      id
      name
      email
    }
    contracts {
      ...ContractFields
    }
    adjustments {
      ...AdjustmentFields
    }
    availableDraftPicks {
      ...DraftPickFields
    }
    capSpacesGiven {
      ...CapSpaceFields
    }
    capSpacesReceived {
      ...CapSpaceFields
    }
  }
  ${FranchiseFields}
  ${ContractFields}
  ${DraftPickFields}
  ${AdjustmentFields}
  ${CapSpaceFields}
`;

export const AuctionItemFields = gql`
  fragment AuctionItemFields on AuctionItem {
    id
    status
    currentBid
    endsAt
    player {
      ...PlayerFields
    }
    leadingFranchise {
      ...FranchiseFields
    }
    bids {
      id
      amount
      createdAt
      franchise {
        id
        name
        logo
      }
    }
  }
  ${PlayerFields}
  ${FranchiseFields}
`;

export const AuctionFields = gql`
  fragment AuctionFields on Auction {
    id
    status
    mode
    currentAuctionItem {
      ...AuctionItemFields
    }
  }
  ${AuctionItemFields}
`;
